import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { mockNotifications } from '../../data/mockData';

const pill = (text) => (
  <Badge variant="outline" className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
    {text}
  </Badge>
);

const categorize = (n) => {
  const t = `${n.title} ${n.message}`.toLowerCase();
  if (t.includes('new lead')) return 'new-lead';
  if (t.includes('approved') || t.includes('approval') || t.includes('sanction')) return 'approval';
  if (t.includes('payout') || t.includes('commission')) return 'payout';
  return 'all';
};

export default function AgentNotifications() {
  const location = useLocation();
  const mode = (() => {
    if (location.pathname.includes('/agent/notifications/new-lead')) return 'new-lead';
    if (location.pathname.includes('/agent/notifications/approval')) return 'approval';
    if (location.pathname.includes('/agent/notifications/payout')) return 'payout';
    return 'all';
  })();

  const notifications = useMemo(() => {
    if (mode === 'all') return mockNotifications;
    return mockNotifications.filter((n) => categorize(n) === mode);
  }, [mode]);

  const title =
    mode === 'new-lead'
      ? 'New Lead Notifications'
      : mode === 'approval'
      ? 'Approval Updates'
      : mode === 'payout'
      ? 'Payout Updates'
      : 'Notifications';

  return (
    <div className="space-y-6" data-testid="agent-notifications">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">{title}</h1>
          <p className="text-muted-foreground mt-1">All important updates in one place</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild size="sm" variant={mode === 'all' ? 'default' : 'outline'}>
            <Link to="/agent/notifications">All</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'new-lead' ? 'default' : 'outline'}>
            <Link to="/agent/notifications/new-lead">New Lead</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'approval' ? 'default' : 'outline'}>
            <Link to="/agent/notifications/approval">Approval</Link>
          </Button>
          <Button asChild size="sm" variant={mode === 'payout' ? 'default' : 'outline'}>
            <Link to="/agent/notifications/payout">Payout</Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {notifications.map((n) => (
          <Card key={n.id} className="border border-border">
            <CardHeader className="space-y-1">
              <div className="flex items-start justify-between gap-3">
                <CardTitle className="text-base">{n.title}</CardTitle>
                <div className="flex gap-2 items-center">
                  {n.read ? pill('Read') : <Badge className="bg-emerald-100 text-emerald-700">New</Badge>}
                  {pill(n.priority)}
                </div>
              </div>
              <CardDescription>{n.timestamp}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{n.message}</p>
            </CardContent>
          </Card>
        ))}
        {notifications.length === 0 && (
          <Card className="border border-border">
            <CardContent className="pt-6 text-center text-muted-foreground">
              No notifications in this category.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

